/**
 * EntityAliasPicker — the data-source picker for widgets in dashboard
 * edit mode.
 *
 * Two steps:
 *   1. Pick an Entity Alias (specific entity or "All X" rollup, or a
 *      raw-tag binding).
 *   2. **Fill the slots** declared by the chosen widget. Each slot lists
 *      every key on the alias; keys whose classified DataKind isn't
 *      compatible with the slot are shown disabled with an explanatory
 *      tooltip.
 *
 * B0.32 — slot-based binding. The flat-key UI from B0.21 is gone; widgets
 * with N keys in different roles (ScatterPlot, CorrelationHeatmap) now
 * show a slot per role.
 */

import { useMemo, useState } from "react";
import { ChevronRight, Database, Search, Sigma, Wrench, X } from "lucide-react";
import { cn } from "@/lib/ui/cn";
import { Badge } from "@/lib/ui/badge";
import { Input } from "@/lib/ui/input";
import { ScrollArea } from "@/lib/ui/scroll-area";
import { usePlant } from "@/lib/plant/plant-store";
import { useOptionalComputedTags } from "@/lib/compute/store";
import {
  bindingError,
  bindingKind,
  classifyKey,
  DATA_KIND_LABEL,
  inferFromTagName,
  storedKindsByName,
  type DataKind,
} from "./data-kinds";
import { readSlot, writeSlot, type DataSource, type WidgetSlot } from "./types";
import { filterKeys } from "./key-search";
import { aliasOfSource, isBoundAlias } from "./alias-source";
import { useQuery } from "@tanstack/react-query";
import { isLiveApi, listOrgTags, twinBindings } from "@/lib/api";

export interface AliasOption {
  /** Display label. */
  label: string;
  /** What this resolves to. */
  resolves: string;
  /** Whether this is an entity-port alias or a raw tag binding. */
  kind: "port" | "tag";
  /** The key list this exposes (port names or tag keys). */
  keys: string[];
  /** The machine's entity id, for an alias that is one machine: a board card binds it by this id (DataSource.byEntity). */
  entityId?: string;
}

export interface EntityAliasPickerProps {
  value?: DataSource;
  onChange: (value: DataSource) => void;
  /** The widget's slot contract. Drives the per-slot UI. */
  slots: WidgetSlot[];
  /** Widget name — used in tooltips. */
  widgetName?: string;
}

export function EntityAliasPicker({
  value,
  onChange,
  slots,
  widgetName = "Widget",
}: EntityAliasPickerProps) {
  const plant = usePlant();
  const computed = useOptionalComputedTags();
  const orgTags = useQuery({
    queryKey: ["org-tags"],
    queryFn: listOrgTags,
    enabled: isLiveApi,
    staleTime: 30_000,
  });
  // The org's tags' stored kinds, so a raw tag's chip is enabled by what it stores, as the board checks it.
  const storedTagKinds = useMemo(() => storedKindsByName(orgTags.data?.items ?? []), [orgTags.data]);
  // Org bindings — so a manual-entry datapoint (a source='manual' tag bound to
  // an entity from the equipment "Manual-entry tags" panel) shows up UNDER its
  // equipment here, not only under the generic "Live tags" alias.
  const orgBindings = useQuery({
    queryKey: ["org-bindings-all"],
    queryFn: () => twinBindings.list(),
    enabled: isLiveApi,
    staleTime: 30_000,
  });
  // entity id → its manual datapoints (name + the tag's real data_kind).
  const manualByEntity = useMemo(() => {
    const m = new Map<string, { name: string; dataKind: DataKind }[]>();
    for (const b of orgBindings.data?.items ?? []) {
      if (b.source !== "manual" || !b.tag_name) continue;
      const arr = m.get(b.entity_id) ?? [];
      arr.push({ name: b.tag_name, dataKind: (b.data_kind || "analog_scalar") as DataKind });
      m.set(b.entity_id, arr);
    }
    return m;
  }, [orgBindings.data]);
  // `${entity label}|${key}` → a manual datapoint's real data_kind, so slot
  // compatibility is precise (e.g. Core_moisture → percentage, not guessed).
  const manualKindByLabelKey = useMemo(() => {
    const m = new Map<string, DataKind>();
    for (const n of plant.nodes) {
      for (const mt of manualByEntity.get(n.id) ?? []) {
        m.set(`${n.instanceName}|${mt.name}`, mt.dataKind);
      }
    }
    return m;
  }, [plant.nodes, manualByEntity]);
  const [step, setStep] = useState<1 | 2>(value ? 2 : 1);
  const [query, setQuery] = useState("");
  // Step 2's tag search, shared by every slot (key-search.ts); a new alias starts it empty.
  const [keyQuery, setKeyQuery] = useState("");

  // Build alias options dynamically from plant entities + a few "All …" rollups
  const aliases = useMemo<AliasOption[]>(() => {
    const specific: AliasOption[] = plant.nodes.map((n) => ({
      label: n.instanceName,
      resolves: n.entity.templateId,
      kind: "port",
      entityId: n.id,
      keys: [
        ...n.entity.inputPorts.map((p) => p.name),
        ...n.entity.outputPorts.map((p) => p.name),
        ...n.entity.parameters.map((p) => p.name),
        // Operator-keyed manual-entry datapoints bound to this entity.
        ...(manualByEntity.get(n.id) ?? []).map((mt) => mt.name),
      ],
    }));

    // Group "All X" rollups for entities that share a template.
    const grouped: Record<string, AliasOption> = {};
    for (const n of plant.nodes) {
      const t = n.entity.templateId;
      if (!grouped[t]) {
        grouped[t] = {
          label: `All ${humaniseTemplate(t)}s`,
          resolves: t,
          kind: "port",
          keys: [
            ...n.entity.inputPorts.map((p) => p.name),
            ...n.entity.outputPorts.map((p) => p.name),
            ...n.entity.parameters.map((p) => p.name),
          ],
        };
      }
    }
    const rollups = Object.values(grouped).filter(
      (g) => plant.nodes.filter((n) => n.entity.templateId === g.resolves).length > 1,
    );

    // Raw-tag pseudo-aliases. Live mode: list the org's REAL tags by name so a
    // widget binds to live readings (resolved by name in use-live-tag-data).
    // Stub mode keeps the demo fixture keys.
    const rawTags: AliasOption[] = isLiveApi
      ? orgTags.data?.items?.length
        ? [
            {
              label: "Live tags",
              resolves: "tag",
              kind: "tag",
              keys: [...new Set(orgTags.data.items.map((t) => t.name).filter(Boolean))],
            },
          ]
        : []
      : [
          { label: "Raw tag", resolves: "tag", kind: "tag", keys: ["furnace_1_bath_temp", "sand_mixer_moisture", "pour_temperature"] },
        ];

    // Computed-tag pseudo-alias — exposes every defined computed tag as
    // a single-key option that downstream surfaces treat exactly like a
    // raw tag binding (kind: "tag"). The classifier reads the declared
    // outputDataKind so slot compatibility filtering works.
    //
    // Live-backed since #165 (before that the store was fixture-only, so this
    // group was always empty in prod). NOTE a computed tag also appears under
    // "Live tags", because GET /v1/iot/tags returns source='computed' rows too.
    // The overlap is deliberate for now: "Live tags" keys by NAME, which is the
    // path use-live-tag-data resolves and the one proven to work in prod, while
    // this group keys by ID to get an exact outputDataKind. Don't drop computed
    // tags from "Live tags" without first confirming in a browser that the
    // id-keyed path actually renders — it has never run against real data.
    const computedTags: AliasOption[] = computed && computed.tags.length > 0
      ? [
          {
            label: "Computed tags",
            resolves: "compute",
            kind: "tag",
            keys: computed.tags.map((t) => t.id),
          },
        ]
      : [];

    return [...rollups, ...specific, ...rawTags, ...computedTags];
  }, [plant.nodes, computed, orgTags.data, manualByEntity]);

  const filtered = aliases.filter((a) =>
    a.label.toLowerCase().includes(query.toLowerCase()) ||
    a.resolves.toLowerCase().includes(query.toLowerCase()),
  );

  const selectedAlias = useMemo(() => aliasOfSource(aliases, value), [aliases, value]);

  const setAlias = (a: AliasOption) => {
    setKeyQuery("");
    // The machine a board card binds (by its entity id) is the one picked: keep that binding rather than turn it into
    // an older one, which reads the machine by its name.
    if (value?.byEntity && isBoundAlias(value, a)) {
      setStep(2);
      return;
    }
    onChange({
      kind: a.kind,
      alias: a.label,
      slots: value?.slots ?? {},
    });
    setStep(2);
  };

  const toggleKeyInSlot = (slot: WidgetSlot, key: string) => {
    if (!value) return;
    const current = readSlot(value, slot.id);
    let next: string[];
    if (slot.multi) {
      next = current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key];
    } else {
      // Single-key slot: clicking the same key clears, otherwise replaces.
      next = current[0] === key ? [] : [key];
    }
    onChange(writeSlot(value, slot.id, next));
  };

  const totalBound = useMemo(() => {
    if (!value?.slots) return 0;
    return Object.values(value.slots).reduce((acc, ks) => acc + ks.length, 0);
  }, [value]);

  return (
    <div className="flex flex-col gap-3">
      {/* Stepper */}
      <div className="flex items-center gap-2 text-[11px]">
        <button
          type="button"
          onClick={() => setStep(1)}
          className={cn(
            "rounded-full px-2 py-0.5 font-medium uppercase tracking-wide",
            step === 1
              ? "bg-brand text-brand-foreground"
              : "bg-surface-muted text-on-surface-muted hover:text-on-surface",
          )}
        >
          1. Alias
        </button>
        <ChevronRight className="size-3 text-on-surface-muted" />
        <button
          type="button"
          onClick={() => value && setStep(2)}
          disabled={!value}
          className={cn(
            "rounded-full px-2 py-0.5 font-medium uppercase tracking-wide",
            step === 2
              ? "bg-brand text-brand-foreground"
              : value
                ? "bg-surface-muted text-on-surface-muted hover:text-on-surface"
                : "bg-surface-muted text-on-surface-muted opacity-50",
          )}
        >
          2. Slots{totalBound > 0 ? ` · ${totalBound}` : ""}
        </button>
      </div>

      {step === 1 && (
        <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-on-surface-muted" />
            <Input
              placeholder="Search entities, templates…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 pl-7 text-xs"
            />
          </div>
          <ScrollArea maxH="max-h-64" label="Data sources" className="flex flex-col gap-1">
            {filtered.map((alias) => (
              <button
                key={`${alias.kind}-${alias.label}`}
                type="button"
                onClick={() => setAlias(alias)}
                className={cn(
                  "flex items-center justify-between gap-2 rounded-md border border-border bg-surface px-2 py-1.5 text-left text-xs transition-colors hover:border-brand hover:bg-brand-muted",
                  isBoundAlias(value, alias) && "border-brand bg-brand-muted",
                )}
              >
                <div className="flex items-center gap-2">
                  {alias.kind === "port" ? (
                    <Wrench className="size-3 text-brand" />
                  ) : alias.label === "Computed tags" ? (
                    <Sigma className="size-3 text-info" />
                  ) : (
                    <Database className="size-3 text-info" />
                  )}
                  <div className="flex flex-col">
                    <span className="font-medium text-on-surface">{alias.label}</span>
                    <span className="font-mono text-[10px] text-on-surface-muted">
                      {alias.resolves}
                    </span>
                  </div>
                </div>
                <Badge variant="secondary" className="text-[10px]">
                  {alias.keys.length} keys
                </Badge>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="py-3 text-center text-[11px] text-on-surface-muted">
                No aliases match "{query}".
              </p>
            )}
          </ScrollArea>
        </>
      )}

      {step === 2 && selectedAlias && (
        <>
          <div className="rounded-md border border-border bg-surface-elevated p-2 text-[11px]">
            <span className="text-on-surface-muted">Alias: </span>
            <span className="font-mono text-on-surface">{selectedAlias.label}</span>
            <span className="ml-2 text-on-surface-muted">→ </span>
            <span className="font-mono text-on-surface-muted">{selectedAlias.resolves}</span>
          </div>

          {slots.length === 0 && (
            <p className="rounded-md border border-dashed border-border bg-surface-elevated/40 p-3 text-[11px] text-on-surface-muted">
              This widget has no data slots — it renders static content.
            </p>
          )}

          {slots.length > 0 && selectedAlias.keys.length > 0 && (
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-on-surface-muted" />
              <Input
                placeholder="Search tags…"
                aria-label="Search tags"
                value={keyQuery}
                onChange={(e) => setKeyQuery(e.target.value)}
                className="h-8 pl-7 text-xs"
              />
            </div>
          )}

          {slots.map((slot) => (
            <SlotSection
              key={slot.id}
              slot={slot}
              alias={selectedAlias}
              value={value!}
              query={keyQuery}
              widgetName={widgetName}
              resolveKind={(key) => {
                // Computed-tag pseudo-alias — look up the declared
                // outputDataKind so slot compatibility is precise (not
                // guessed from the tag id).
                if (selectedAlias.label === "Computed tags" && computed) {
                  const t = computed.byId(key);
                  if (t) return t.outputDataKind;
                }
                // A raw tag: its stored data_kind, as the board's binding check reads it (bindingKind).
                if (selectedAlias.kind === "tag") {
                  const source = { kind: "tag", alias: selectedAlias.label };
                  return bindingKind(source, key, plant.nodes, storedTagKinds, slot.dataKinds) ?? inferFromTagName(key);
                }
                // Manual-entry datapoint on this entity → its real data_kind.
                const mk = manualKindByLabelKey.get(`${selectedAlias.label}|${key}`);
                if (mk) return mk;
                return classifyKey(plant.nodes, selectedAlias.label, key);
              }}
              onToggle={(key) => toggleKeyInSlot(slot, key)}
              onClear={() =>
                onChange(writeSlot(value!, slot.id, []))
              }
            />
          ))}
        </>
      )}
    </div>
  );
}

/**
 * One slot row — header + chip-style key list with disabled keys.
 */
export function SlotSection({
  slot,
  alias,
  value,
  query = "",
  widgetName,
  resolveKind,
  onToggle,
  onClear,
}: {
  slot: WidgetSlot;
  alias: AliasOption;
  value: DataSource;
  /** Step 2's tag search: only matching keys show, and the slot's bound keys always do (filterKeys). */
  query?: string;
  widgetName: string;
  resolveKind: (key: string) => DataKind;
  onToggle: (key: string) => void;
  onClear: () => void;
}) {
  const bound = readSlot(value, slot.id);
  const searching = query.trim() !== "";
  const visible = filterKeys(alias.keys, query, new Set(bound));

  return (
    <section className="flex flex-col gap-1.5 rounded-md border border-border bg-surface p-2">
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-on-surface">
            {slot.label}
          </span>
          {slot.required && (
            <Badge variant="warning" className="text-[9px]">
              required
            </Badge>
          )}
          <Badge variant="secondary" className="text-[9px]">
            {slot.multi ? "multi" : "single"}
          </Badge>
        </div>
        {bound.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="text-[10px] text-on-surface-muted hover:text-danger"
          >
            <X className="inline size-3" /> clear
          </button>
        )}
      </header>

      {/* Allowed kinds */}
      <div className="flex flex-wrap gap-1">
        {slot.dataKinds.map((k) => (
          <span
            key={k}
            className="rounded-full bg-surface-muted px-1.5 py-0.5 font-mono text-[9px] text-on-surface-muted"
          >
            {DATA_KIND_LABEL[k]}
          </span>
        ))}
      </div>

      {slot.hint && (
        <p className="text-[10px] leading-snug text-on-surface-muted">
          {slot.hint}
        </p>
      )}

      {searching && alias.keys.length > 0 && (
        <p className="text-[10px] text-on-surface-muted">
          showing {visible.length.toLocaleString()} of {alias.keys.length.toLocaleString()}
        </p>
      )}

      {/* Key chips */}
      <div className="flex flex-wrap gap-1">
        {alias.keys.length === 0 && (
          <p className="text-[10px] italic text-on-surface-muted">
            (no keys on this alias)
          </p>
        )}
        {alias.keys.length > 0 && visible.length === 0 && (
          <p className="text-[10px] italic text-on-surface-muted">No tags match "{query.trim()}".</p>
        )}
        {visible.map((key) => {
          const kind: DataKind = resolveKind(key);
          const compatible = slot.dataKinds.includes(kind);
          const active = bound.includes(key);
          const tooltip = compatible
            ? `${key} · ${DATA_KIND_LABEL[kind]}`
            : bindingError(widgetName, slot.label, slot.dataKinds, kind, key);

          return (
            <button
              key={key}
              type="button"
              disabled={!compatible}
              onClick={() => onToggle(key)}
              title={tooltip}
              className={cn(
                "flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-mono transition-colors",
                !compatible &&
                  "cursor-not-allowed border-border bg-surface-muted/50 text-on-surface-muted opacity-50",
                compatible &&
                  !active &&
                  "border-border bg-surface-elevated text-on-surface-muted hover:border-on-surface hover:text-on-surface",
                compatible &&
                  active &&
                  "border-brand bg-brand text-brand-foreground",
              )}
            >
              <span>{key}</span>
              <span
                className={cn(
                  "rounded-full px-1 py-0 text-[9px]",
                  active ? "bg-brand-foreground/20" : "bg-surface-muted/60",
                )}
              >
                {DATA_KIND_LABEL[kind]}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function humaniseTemplate(t: string): string {
  return (t.split(".").pop() ?? t)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}
